import { TableRow, TableCell, Grid, Typography, TextField, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Alert, Snackbar, Box, Tooltip, useMediaQuery } from '@mui/material';
import { Item, UpdateItemParRequest, client } from '../../../app/services/api/orderManagementClient';
import { ChangeEvent, forwardRef, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyRoundAndFormat } from '../../../utils';
import useLogError from '../../../hooks/useLogError';
import { UserContext } from '../../../components/shared/useUser';
import { theme } from '../../../theme';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';
import TextWithOverflowTooltip from '../../../components/shared/TextWithOverflowTooltip';
import { useNavigate } from 'react-router-dom';

type ParItemsRowProps = {
  product: Item;
  currencyCode?: string;
  showMessageWhenNoCost: boolean;
}

const ParItemRow = forwardRef<{ matchPredictedQuantities: () => void } | null, ParItemsRowProps>(({ product, currencyCode, showMessageWhenNoCost }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('parConfiguration');
  const { logError } = useLogError();
  const { selectedStore } = useContext(UserContext);
  const [par1, setPar1] = useState<number>(product.par1 ?? 0);
  const [par2, setPar2] = useState<number>(product.par2 ?? 0);
  const [loading, setLoading] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { hasPermissionTo } = useContext(UserContext);
  const PARConfigurationWrite = hasPermissionTo(['PARConfigurationWrite']);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const onParChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, isPar1: boolean) => {
    event.preventDefault();
    event.target.value = event.target.value.replace(/^0+/, '');
    const value = Math.abs(Number(event.target.value|| 0));
    if (isPar1) {
      setPar1(value);
    }
    else {
      setPar2(value);
    }
  };

  const onParUpdate = async (itemId: number, par1: number, par2: number) => {
    try {
      setLoading(true);
      const request: UpdateItemParRequest = {
        itemId,
        par1,
        par2,
      };
      const response = await client.updateItemPar(selectedStore?.storeNumber, request);
      if (response.success) {
        handleSnackbarOpen(t('itemUpdatedSuccessfully'), 'success');
      }
      else {
        handleSnackbarOpen(response.message, 'warning');
      }
    } catch (error) {
      handleSnackbarOpen(t('itemupdateerror') + ' ' + error, 'error');
      logError(error);
    } finally {
      setLoading(false);
      setShowDialog(false);
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const hideDialog = () => {
    setShowDialog(false);
  };

  return (
    <>
      <TableRow key={product.id}>
        <TableCell sx={{ width: '60%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Grid container
            alignItems={'center'}
            spacing={1}
            onClick={() => navigate(`/store/item/${product.id}?sku=${product.sku}&orderable=true`)}
            wrap="nowrap">
            <Grid item
              xs="auto">
              <img
                style={{
                  objectFit: product.thumbnailUrl ? 'contain' : 'cover',
                  width: '150px',
                  height: isLargeScreen ? '75px' : '150px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.custom.gray[200]}`, 
                  background: theme.palette.common.white,
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  display: 'block',
                  margin: '0',
                  padding: '0',
                }}
                alt="item-thumbnail"
                src={
                  product.thumbnailUrl
                    ? product.thumbnailUrl
                    : 'https://placehold.co/150x150'
                }
                loading="lazy"
              />
            </Grid>
            <Grid item
              sx={{ minWidth: 0, paddingLeft: '10px !important', flex: 1 }}>
              {isLargeScreen ? (
                <Grid container
                  spacing={2}>
                  <Grid item
                    xs={6}>
                    <TextWithOverflowTooltip text={product.name} />
                    <TextWithOverflowTooltip text={product.sku} />
                    <TextWithOverflowTooltip text={isOrderCategory(product.category)
                      ? t(OrderCategoriesTranslationMap[product.category as keyof typeof OrderCategoriesTranslationMap])
                      : product.category} />
                    {(showMessageWhenNoCost || product.price === 0) ?
                      <TextWithOverflowTooltip text='' />
                      :
                      <TextWithOverflowTooltip text={applyRoundAndFormat(product.price, currencyCode)} />
                    }
                  </Grid>
                  <Grid item
                    xs={6}>
                    <TextWithOverflowTooltip text={`${t('orderableUom')}: ${product.orderableUom} (${product.packSize}${product.primaryUom})`} />
                    <TextWithOverflowTooltip text={`${t('primaryUom')}: ${product.primaryUom}`} />
                    <Tooltip title={`${t('stockOnHand')}: ${product.stockOnHand}`}
                      arrow>
                      <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                        {t('soh')} {product.stockOnHand}
                      </Box>
                    </Tooltip>
                    <Tooltip title={`${t('stockOnOrder')}: ${product.stockOnOrder}`}
                      arrow>
                      <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                        {t('soo')}: {product.stockOnOrder}
                      </Box>
                    </Tooltip>
                  </Grid>
                </Grid>
              ) : (
                <>
                  {product.name}  <br />
                  {product.sku}   <br />
                  {isOrderCategory(product.category)
                    ? t(OrderCategoriesTranslationMap[product.category as keyof typeof OrderCategoriesTranslationMap])
                    : product.category}   <br />
                  {(showMessageWhenNoCost || product.price === 0) ?
                    ''
                    :
                    applyRoundAndFormat(product.price, currencyCode)
                  } <br />
                  {t('orderableUom')}: {product.orderableUom} ({product.packSize}{product.primaryUom})<br />
                  {t('primaryUom')}: {product.primaryUom}  <br />
                  <Tooltip title={`${t('stockOnHand')}: ${product.stockOnHand}`}
                    arrow>
                    <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                      {t('soh')} {product.stockOnHand}
                    </Box>
                  </Tooltip>
                  <Tooltip title={`${t('stockOnOrder')}: ${product.stockOnOrder}`}
                    arrow>
                    <Box style={{ whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip' }}>
                      {t('soo')}: {product.stockOnOrder}
                    </Box>
                  </Tooltip>
                </>
              )}
            </Grid>
          </Grid>
        </TableCell>
        <TableCell>
          <Box
            sx={{
              display: 'flex',
              position: 'relative',
              maxWidth: 100,
              '& input': {
                appearance: 'textfield',
                MozAppearance: 'textfield',
                '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                  display: 'none',
                },
                textAlign: 'center',
              },
              '& button': {
                position: 'absolute',
                right: -20,
                top: 15,
                '& svg': {
                  fill: '#fff',
                },
              },
            }}
          >
            <TextField
              size="small"
              type="number"
              autoComplete="off"
              sx={{ maxWidth: 100, minWidth:70, width: '100%' }}
              disabled={loading || !PARConfigurationWrite}
              value={par1}
              onChange={(e) => onParChange(e, true)}
            />
          </Box>
        </TableCell> 
        <TableCell>
          <Box
            sx={{
              display: 'flex',
              position: 'relative',
              maxWidth: 100,
              '& input': {
                appearance: 'textfield',
                MozAppearance: 'textfield',
                '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                  display: 'none',
                },
                textAlign: 'center',
              },
              '& button': {
                position: 'absolute',
                right: -20,
                top: 15,
                '& svg': {
                  fill: '#fff',
                },
              },
            }}
          >
            <TextField
              size="small"
              type="number"
              autoComplete="off"
              sx={{ maxWidth: 100, minWidth:70, width: '100%' }}
              disabled={loading || !PARConfigurationWrite}
              value={par2}
              onChange={(e) => onParChange(e, false)}
            />
          </Box>

        </TableCell>
        <TableCell>
          <Button
            disabled={loading || !PARConfigurationWrite}
            variant="primary"
            onClick={() => setShowDialog(true)}
            size="lg"
          >
            {t('updateQuantity')}
          </Button>
        </TableCell>
      </TableRow>
      <Dialog open={showDialog}
        onClose={() => setShowDialog(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>
          {t('confirmDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item
              xs={12}>
              <Typography variant='textMD'>{t('confirmDialogContent', { item: product.name })}</Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions >
          <Button
            variant="secondary"
            onClick={hideDialog}>
            {t('cancel')}
          </Button>
          <Button variant="primary"
            onClick={() => onParUpdate(product.id, Number(par1), Number(par2))}
            disabled={loading}>
            {t('update')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
});

ParItemRow.displayName = 'ParItemRow';

export default ParItemRow;